import { useMemo } from "react";
import { useFetchActions } from "@core/features/actions";
import { getOrganizationsFromActions } from "@core/features/actions";

type Args = {
  /** The organization ID to check authorization for */
  organizationId: number;
  /** Current user (to fetch actions) */
  user: { id: number } | null;
  /** Optional program ID to scope the action fetch */
  programId?: number;
};

type UseCanManageOrganizationResult = {
  /** Whether the user is authorized to manage the specified organization */
  isAuthorized: boolean;
  /** Whether the authorization check is still loading */
  isLoading: boolean;
  /** The organization ID the user is allowed to manage (null if none) */
  allowedOrgId: number | null;
};

/**
 * Hook to check if a user can manage a specific organization.
 *
 * Fetches the user's actions and checks if they have an org admin action
 * (org_manage) for the specified organization ID.
 *
 * @example
 * ```typescript
 * const { isAuthorized, isLoading } = useCanManageOrganization({
 *   organizationId: 123,
 *   user,
 * });
 *
 * if (isLoading) return <Loader />;
 * if (!isAuthorized) return <Navigate to="/dashboard" />;
 * ```
 */
export function useCanManageOrganization({
  organizationId,
  user,
  programId,
}: Args): UseCanManageOrganizationResult {
  const { data: actionsData, isLoading } = useFetchActions({
    programId,
    user,
    isEnabled: !!user,
  });

  const isAuthorized = useMemo(() => {
    if (!actionsData?.actions) return false;
    const orgs = getOrganizationsFromActions(actionsData.actions);
    return orgs.some((org) => org.id === organizationId);
  }, [actionsData?.actions, organizationId]);

  const allowedOrgId = isAuthorized ? organizationId : null;

  return {
    isAuthorized,
    isLoading,
    allowedOrgId,
  };
}
